import { useLayoutEffect, useMemo, useRef, useState, type RefObject } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, usePropertyField } from '@vibesites/runtime/react';
import { RichTextContent } from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

/** Keep the hero title below the fixed header; shrink when the name is long. */
const HERO_TITLE_MIN_PX = 28;
const HERO_TITLE_HEADER_GAP_PX = 56;

function useFitHeroTitle(wrapRef: RefObject<HTMLDivElement | null>) {
  const [fontPx, setFontPx] = useState<number | null>(null);

  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;

    let raf = 0;

    const fit = () => {
      const el = wrap.querySelector('.hero-title') as HTMLElement | null;
      if (!el) return;

      const header = document.querySelector('header.header-tower') as HTMLElement | null;
      const limit =
        (header?.getBoundingClientRect().bottom ?? 72) + HERO_TITLE_HEADER_GAP_PX;

      const prevAnimation = el.style.animation;
      const prevTransform = el.style.transform;
      const prevFontSize = el.style.fontSize;

      // Measure at the CSS clamp size without the entrance transform.
      el.style.animation = 'none';
      el.style.transform = 'none';
      el.style.fontSize = '';
      void el.offsetHeight;

      const maxPx = parseFloat(getComputedStyle(el).fontSize);
      if (!Number.isFinite(maxPx) || maxPx <= 0) {
        el.style.animation = prevAnimation;
        el.style.transform = prevTransform;
        el.style.fontSize = prevFontSize;
        return;
      }

      if (el.getBoundingClientRect().top >= limit) {
        el.style.animation = prevAnimation;
        el.style.transform = prevTransform;
        el.style.fontSize = prevFontSize;
        setFontPx((prev) => (prev == null ? prev : null));
        return;
      }

      let lo = HERO_TITLE_MIN_PX;
      let hi = maxPx;
      let best = HERO_TITLE_MIN_PX;
      for (let i = 0; i < 18; i++) {
        const mid = (lo + hi) / 2;
        el.style.fontSize = `${mid}px`;
        if (el.getBoundingClientRect().top >= limit) {
          best = mid;
          lo = mid;
        } else {
          hi = mid;
        }
      }

      el.style.animation = prevAnimation;
      el.style.transform = prevTransform;
      el.style.fontSize = prevFontSize;
      setFontPx((prev) => (prev != null && Math.abs(prev - best) < 0.5 ? prev : best));
    };

    const scheduleFit = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(fit);
    };

    scheduleFit();

    const ro = new ResizeObserver(scheduleFit);
    ro.observe(wrap);
    const section = wrap.closest('section');
    if (section) ro.observe(section);
    const header = document.querySelector('header.header-tower');
    if (header) ro.observe(header);

    const mo = new MutationObserver(scheduleFit);
    mo.observe(wrap, { characterData: true, childList: true, subtree: true });

    window.addEventListener('resize', scheduleFit);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener('resize', scheduleFit);
    };
  }, [wrapRef]);

  return fontPx;
}

// ============================================================================
// Why this fork exists
// ----------------------------------------------------------------------------
// Forked from `@vibesites/theme-rise`'s Hero with Weidner-only layout edits:
//   1. Pre-title and CTA link removed (schema + JSX).
//   2. Title is a step below the previous 8rem cap, and glow/text-shadow
//      is removed. Long property names auto-shrink so the title never
//      rises into the fixed header.
//   3. Content block sits below center (`justify-end` + ~48–56vh bottom
//      padding on a 140vh section) rather than dead-centered or flush to
//      the bottom — keeps the title at the same viewport position as the
//      old 120vh / 28–36vh setup while showing more image below.
//   4. Section height bumped from `h-screen` to `h-[140vh]` so more of
//      the background image shows before the next section.
//   5. Secondary-token tagline (`hero.tagline`) below the subtitle.
//   6. Parallax scroll and Ken Burns animation on the background image
//      removed (image stays static in the section).
//   7. Woodin Creek Village: tagline default is "Wine • Dine • Live".
//
// Maintenance checklist when re-syncing with the rise parent:
//   1. Diff this file against the rise Hero in the sibling checkout.
//   2. Pull non-layout changes (overlay, a11y) — do not reintroduce the
//      parallax scroll handler or Ken Burns animation.
//   3. Keep pre_title / cta_link out; keep the reduced title + no glow;
//      keep lower content positioning; keep hero.tagline.
//   4. Run `pnpm run lint:schema` and `pnpm run typecheck`.
// ============================================================================

/**
 * Formats a raw bedrooms range (e.g. "studio - 2") into natural copy
 * like "Studio - 2 Bedroom". Handles single values ("1" → "1 Bedroom"),
 * ranges ("1 - 3" → "1 - 3 Bedroom"), and studios. Equal endpoints
 * collapse to a single value (e.g. "2 - 2" → "2 Bedroom",
 * "Studio - Studio" → "Studio").
 */
function formatBedroomsLabel(raw: string | null | undefined): string {
  if (!raw) return 'Apartments';
  const trimmed = raw.trim();
  if (!trimmed) return 'Apartments';

  const parts = trimmed.split(/\s*-\s*/);
  const formatted = parts.map((p) => {
    const lower = p.trim().toLowerCase();
    if (lower === 'studio' || lower === 'std') return 'Studio';
    return p.trim();
  });

  // Collapse equal endpoints: "2 - 2" → "2", "Studio - Studio" → "Studio".
  // Compare case-insensitively so "studio - Studio" still collapses cleanly.
  const deduped =
    formatted.length > 1 && formatted.every((p) => p.toLowerCase() === formatted[0].toLowerCase())
      ? [formatted[0]]
      : formatted;

  const label = deduped.join(' - ');
  const allStudios = deduped.every((p) => p === 'Studio');
  return allStudios ? `${label}` : `${label} Bedroom`;
}

const featuredImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Property hero image',
    },
  },
};

export function Hero() {
  const bgImage = useSlotGroup('hero.bg_image', featuredImageDef);
  const titleWrapRef = useRef<HTMLDivElement>(null);
  const titleFontPx = useFitHeroTitle(titleWrapRef);

  const bedroomsRange = usePropertyField('property_bedrooms_range');
  const city = usePropertyField('property_address_city');
  const state = usePropertyField('property_address_state');

  const subtitleHtml = useMemo(() => {
    const beds = formatBedroomsLabel(bedroomsRange as string | null);
    const cityStr = city || '';
    const stateStr = state || '';
    const location = [cityStr, stateStr].filter(Boolean).join(', ');
    return `<p>${beds} Apartments for Rent${location ? ` in ${location}` : ''}</p>`;
  }, [bedroomsRange, city, state]);

  return (
    <section className="relative w-full h-[140vh] min-h-screen overflow-hidden">
      {/* Static background (no parallax or Ken Burns) */}
      <EditableSlotGroup groupPath="hero.bg_image" definition={featuredImageDef}>
        <div className="absolute inset-0">
          {bgImage.image ? (
            <VibesitesImage
              url={bgImage.image}
              alt={bgImage.alt || ''}
              priority
              sizes="100vw"
              objectFit="cover"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-[var(--vibe-color-text)]" />
          )}
        </div>
      </EditableSlotGroup>

      {/* Gradient overlay for text contrast — purely decorative, must not
          intercept clicks (otherwise floating chatbot iframes / widgets
          rendered above the hero can't be clicked through the empty
          gradient area). */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/35 to-black/20 pointer-events-none" />

      {/* Content. Anchored toward the lower portion of the hero so the
          title/subtitle sit lower on the background image. Pointer events
          stay off on the wrapper and on only on interactive children so
          empty hero space passes clicks through (e.g. chat widgets). */}
      <div className="relative z-10 flex flex-col items-center justify-end h-full text-center text-white px-6 sm:px-8 pb-[48vh] sm:pb-[52vh] md:pb-[56vh] pointer-events-none">
        <div ref={titleWrapRef} className="w-full max-w-5xl">
          <RichTextContent
            path="hero.title"
            label="Title"
            defaultValue='<p><span data-type="field-token" data-field="property_name"></span></p>'
            as="h1"
            className="hero-title hero-entrance hero-entrance-1 font-heading font-bold mb-5 leading-[1.1] tracking-tight drop-shadow-lg uppercase break-words pointer-events-auto"
            style={titleFontPx != null ? { fontSize: `${titleFontPx}px` } : undefined}
          />
        </div>

        <RichTextContent
          path="hero.subtitle"
          label="Subtitle"
          defaultValue={subtitleHtml}
          as="p"
          className="hero-entrance hero-entrance-2 font-body text-lg sm:text-xl md:text-2xl max-w-5xl mb-4 leading-relaxed opacity-90 drop-shadow-md uppercase pointer-events-auto"
        />

        <RichTextContent
          path="hero.tagline"
          label="Tagline"
          defaultValue="<p>Wine • Dine • Live</p>"
          as="p"
          className="hero-entrance hero-entrance-3 font-body font-bold text-sm sm:text-base md:text-lg max-w-5xl mb-0 leading-relaxed tracking-wide uppercase drop-shadow-md text-secondary pointer-events-auto"
        />
      </div>

      <style>{`
        /* Fluid default; JS shrinks further when a long name would hit the nav. */
        .hero-title {
          font-size: clamp(2.25rem, 8vw, 6.5rem);
        }
        @keyframes heroEntrance {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hero-entrance {
          animation: heroEntrance 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .hero-entrance-1 { animation-delay: 0.1s; }
        .hero-entrance-2 { animation-delay: 0.3s; }
        .hero-entrance-3 { animation-delay: 0.5s; }
        @media (prefers-reduced-motion: reduce) {
          .hero-entrance { animation: none; opacity: 1; transform: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Hero',
  displayName: 'Hero Section',
  tokens: {
    'hero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue:
        '<p><span data-type="field-token" data-field="property_name"></span></p>',
    },
    'hero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Apartments for Rent</p>',
    },
    'hero.tagline': {
      label: 'Tagline',
      type: 'richtext',
      defaultValue: '<p>Wine • Dine • Live</p>',
    },
  },
  slotGroups: {
    'hero.bg_image': featuredImageDef,
  },
};

(Hero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Hero;
